// GatedVideoPlayer.jsx — VideoPlayer that checks permission first.
//
// Drop-in replacement for `<VideoPlayer videoUrl={apiService.getVideoUrl(id)} .../>`. That older
// pattern built a playable URL straight from an id and never asked the server whether the
// viewer was allowed, which walked past the sign-in wall — the streaming endpoint has to stay
// open because a <video> tag cannot send an auth header.
//
// Take this component, not VideoPlayer, anywhere a video is played from an id. A new surface
// then inherits the wall instead of having to remember it.
import React from "react";
import VideoPlayer from "./VideoPlayer";
import LockedVideo from "./LockedVideo";
import { useVideoAccess } from "../hooks/useVideoAccess";
import { useT } from "../i18n/useT";

const GatedVideoPlayer = ({
  videoId,
  title,
  seed = null,
  justCreated = false,
  // The locked card carries its own height — the sign-in form inside it sets that, and a fixed
  // 16:9 box clips the form on a narrow window. The placeholder and the restricted notice below
  // keep the aspect, since those really are just boxes.
  lockedClassName = "w-full",
  placeholderClassName = "w-full aspect-video",
  ...playerProps
}) => {
  const t = useT();
  const access = useVideoAccess(videoId, seed);

  if (access.requiresSignIn) {
    return (
      <LockedVideo
        videoId={videoId}
        title={title || access.title}
        justCreated={justCreated}
        className={lockedClassName}
      />
    );
  }

  if (access.error === "restricted") {
    return (
      <div className={`flex items-center justify-center rounded-2xl bg-gray-900 text-gray-300 text-sm px-6 text-center ${placeholderClassName}`}>
        {t("video_restricted")}
      </div>
    );
  }

  // Hold the player's footprint while the check is in flight, so the layout does not jump.
  if (!access.videoUrl) {
    return <div className={`rounded-2xl bg-gray-900 ${placeholderClassName}`} />;
  }

  return (
    <VideoPlayer
      videoUrl={access.videoUrl}
      subtitleUrl={access.subtitleUrl}
      title={title || access.title || "Generated Video"}
      {...playerProps}
    />
  );
};

export default GatedVideoPlayer;
